import { useQuery } from '@apollo/client'
import { Clear, Search } from '@mui/icons-material'
import {
    Box,
    Button,
    CircularProgress,
    InputAdornment,
    Stack,
    TablePagination,
    TextField,
    Typography,
} from '@mui/material'
import {
    GridFooterContainer,
    GridPreferencePanelsValue,
    useGridApiContext,
} from '@mui/x-data-grid-pro'
import { gql } from '__generated__'
import { GetCompaniesById } from 'candidate/graphql/generatedProfile.gql'
import startCase from 'lodash.startcase'
import { type FC, useEffect, useState } from 'react'
import {
    GetIndustryOptions,
    GetSectorOptions,
} from 'recruiter/graphql/companies.gql'
import { GetCandidateNoteTemplates } from 'recruiter/graphql/candidateNotes.gql'
import { useCandidateSqlSearch } from 'recruiter/hooks'
import { GetProcessStatuses } from 'shared/graphql/candidateProcessStatus.gql'
import { GetColleges } from 'shared/graphql/colleges.gql'
import { GetDegreeTypes } from 'shared/graphql/degreesTypes.gql'
import { CandidateFilterState } from 'shared/hooks/candidateSearchUrlState'
import { getPxAsRem } from 'shared/utils'
import { GetOfficeLocationsById } from 'shared/components/OfficeLocationsAutocomplete/OfficeLocationsAutocomplete.gql'

const GetGenderOptions = gql(/* GraphQL */ `
    query GetGenderOptionsForChips {
        genders {
            id
            value
        }
    }
`)

const GetEthnicityOptions = gql(/* GraphQL */ `
    query GetEthnicityOptionsForChips {
        ethnicities {
            id
            value
        }
    }
`)

const GetVisaStatusOptions = gql(/* GraphQL */ `
    query GetVisaStatusOptionsForChips {
        visaStatuses {
            id
            value
        }
    }
`)

const GetCitizenshipOptions = gql(/* GraphQL */ `
    query GetCitizenshipOptionsForChips {
        citizenships {
            id
            value
        }
    }
`)

const GetMbaTierOptions = gql(/* GraphQL */ `
    query GetMbaTierOptionsForChips {
        mbaTiers {
            id
            value
        }
    }
`)

const GetMajorOptions = gql(/* GraphQL */ `
    query GetMajorOptionsForChips {
        majors {
            id
            value
        }
    }
`)

const GetQualitativeOptions = gql(/* GraphQL */ `
    query GetQualitativeOptionsForChips {
        personalityAttributes {
            id
            value
        }
    }
`)

const GetRevenueExperienceOptions = gql(/* GraphQL */ `
    query GetRevenueExperienceOptionsForChips {
        revenueExperiences {
            id
            value
        }
    }
`)

const GetLgbtqOptions = gql(/* GraphQL */ `
    query GetLgbtqOptionsForChips {
        lgbtq {
            id
            value
        }
    }
`)

const GetCpiPlacedOptions = gql(/* GraphQL */ `
    query GetCpiPlacedOptionsForChips {
        cpiPlaced {
            id
            value
        }
    }
`)

const GetReferenceAttributeOptions = gql(/* GraphQL */ `
    query GetReferenceAttributeOptionsForChips {
        referenceAttributes {
            id
            value
        }
    }
`)

interface LookupData {
    companies: Array<{ id: string; value: string }>
    locations: Array<{ id: string; value: string }>
    genders: Array<{ id: string; value: string }>
    ethnicities: Array<{ id: string; value: string }>
    citizenships: Array<{ id: string; value: string }>
    visaStatuses: Array<{ id: string; value: string }>
    qualitatives: Array<{ id: string; value: string }>
    revenueExperiences: Array<{ id: string; value: string }>
    lgbtq: Array<{ id: string; value: string }>
    cpiPlaced: Array<{ id: string; value: string }>
    referenceAttributes: Array<{ id: string; value: string }>
    colleges: Array<{ id: string; value: string }>
    majors: Array<{ id: string; value: string }>
    degreeTypes: Array<{ id: string; value: string }>
    mbaTiers: Array<{ id: string; value: string }>
    industries: Array<{ id: string; value: string }>
    sectors: Array<{ id: string; value: string }>
    screeningTemplates: Array<{ id: string; templateType: string }>
    processStatuses: Array<{
        id: string
        label: string
        groupId: string
        groupLabel: string
    }>
}

const RECRUITING_STATUS_LABELS: Record<string, string> = {
    ActivelyRecruiting: 'Actively Recruiting',
    FinalStages: 'Final Stages',
    Opportunistic: 'Opportunistic',
    HoldingOut: 'Holding Out',
    NotRecruiting: 'Not Recruiting',
    DoNotCall: 'Do Not Call',
    NoStatus: 'No Status',
}

const OUTREACH_STATUS_LABELS: Record<string, string> = {
    TreadCarefully: 'Tread Carefully',
    NonPoach: 'Non-Poach',
    CpiPlaced: 'CPI Placed',
    NoStatus: 'No Status',
}

const ghostInputSx = {
    '& .MuiOutlinedInput-root': {
        height: 28,
        fontSize: '0.8125rem',
        paddingLeft: '6px',
        borderRadius: 0,
        '& fieldset': {
            border: 'none',
            borderBottom: '1px solid',
            borderColor: 'divider',
        },
        '&.Mui-focused': {
            '& fieldset': { borderColor: 'primary.main' },
        },
    },
    '& .MuiOutlinedInput-input': { padding: '4px 4px' },
    '& .MuiInputAdornment-root': { marginRight: '2px' },
}

const minimalTagSx = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    fontSize: '0.6875rem',
    color: 'text.secondary',
    bgcolor: 'grey.100',
    px: 1,
    py: 0.25,
    borderRadius: 1,
    cursor: 'pointer',
    '&:hover': { bgcolor: 'grey.200' },
}

interface FilterConfig {
    key: string
    label: string
    isActive: (f: CandidateFilterState) => boolean
    getDisplayValue: (
        f: CandidateFilterState,
        lookup: LookupData
    ) => string | null
    getClearFields: () => Partial<CandidateFilterState>
}

const pluralize = (count: number, singular: string) =>
    count === 1 ? `1 ${singular}` : `${count} ${singular}s`

const FILTER_CONFIGS: FilterConfig[] = [
    {
        key: 'quickSearch',
        label: 'Search',
        isActive: (f) => !!f.quickSearch,
        getDisplayValue: (f) => f.quickSearch || null,
        getClearFields: () => ({ quickSearch: undefined }),
    },
    {
        key: 'recruitingStatus',
        label: 'Recruiting Status',
        isActive: (f) => f.recruitingStatus.length > 0,
        getDisplayValue: (f) =>
            f.recruitingStatus
                .map((s) => RECRUITING_STATUS_LABELS[s] || s)
                .join(', '),
        getClearFields: () => ({ recruitingStatus: [] }),
    },
    {
        key: 'outreachStatus',
        label: 'Outreach',
        isActive: (f) => f.outreachStatus.length > 0,
        getDisplayValue: (f) =>
            f.outreachStatus
                .map((s) => OUTREACH_STATUS_LABELS[s] || s)
                .join(', '),
        getClearFields: () => ({ outreachStatus: [] }),
    },
    {
        key: 'starRating',
        label: 'Average Star Rating',
        isActive: (f) => f.starRatingMin !== undefined,
        getDisplayValue: (f) =>
            f.starRatingMin !== undefined
                ? `${f.starRatingMin}${f.starRatingMin < 5 ? '+' : ''} stars`
                : null,
        getClearFields: () => ({
            starRatingMin: undefined,
            starRatingMax: undefined,
        }),
    },
    {
        key: 'companyIds',
        label: 'Company',
        isActive: (f) => f.companyIds.length > 0,
        getDisplayValue: (f, lookup) => {
            const names = f.companyIds
                .map((id) => lookup.companies.find((c) => c.id === id)?.value)
                .filter(Boolean)
            return names.length > 0
                ? names.join(', ')
                : pluralize(f.companyIds.length, 'selected')
        },
        getClearFields: () => ({
            companyIds: [],
            companyTimeFrame: undefined,
            companyRangeGte: undefined,
            companyRangeLte: undefined,
        }),
    },
    {
        key: 'industryNames',
        label: 'Industry',
        isActive: (f) => f.industryNames.length > 0,
        getDisplayValue: (f) => f.industryNames.join(', '),
        getClearFields: () => ({
            industryNames: [],
            industryTimeFrame: undefined,
            industryRangeGte: undefined,
            industryRangeLte: undefined,
        }),
    },
    {
        key: 'sectorNames',
        label: 'Sector',
        isActive: (f) => f.sectorNames.length > 0,
        getDisplayValue: (f) =>
            f.sectorNames
                .map((s) => (s === 'NO_SECTORS' ? 'No sectors listed' : s))
                .join(', '),
        getClearFields: () => ({
            sectorNames: [],
            sectorTimeFrame: undefined,
            sectorRangeGte: undefined,
            sectorRangeLte: undefined,
        }),
    },
    {
        key: 'locationIds',
        label: 'Location',
        isActive: (f) => f.locationIds.length > 0,
        getDisplayValue: (f, lookup) => {
            const names = f.locationIds
                .map((id) => lookup.locations.find((l) => l.id === id)?.value)
                .filter(Boolean)
            return names.length > 0
                ? names.join(', ')
                : pluralize(f.locationIds.length, 'selected')
        },
        getClearFields: () => ({
            locationIds: [],
            locationTimeFrame: undefined,
            locationRangeGte: undefined,
            locationRangeLte: undefined,
            locationIncludeStateCountry: false,
        }),
    },
    {
        key: 'locationText',
        label: 'Location',
        isActive: (f) => f.locationText.length > 0,
        getDisplayValue: (f) =>
            f.locationText
                .map((v) => (v.startsWith(', ') ? v.slice(2) : v))
                .join(', '),
        getClearFields: () => ({
            locationText: [],
            locationTextTimeFrame: undefined,
            locationTextRangeGte: undefined,
            locationTextRangeLte: undefined,
        }),
    },
    {
        key: 'positionTitles',
        label: 'Position',
        isActive: (f) => f.positionTitles.length > 0,
        getDisplayValue: (f) => f.positionTitles.join(', '),
        getClearFields: () => ({
            positionTitles: [],
            positionTimeFrame: undefined,
            positionRangeGte: undefined,
            positionRangeLte: undefined,
        }),
    },
    {
        key: 'searchesFilter',
        label: 'Searches',
        isActive: (f) => f.searchFilters.length > 0,
        getDisplayValue: (f) => pluralize(f.searchFilters.length, 'filter'),
        getClearFields: () => ({
            searchFilters: [],
        }),
    },
    {
        key: 'genderIds',
        label: 'Gender',
        isActive: (f) => f.genderIds.length > 0,
        getDisplayValue: (f, lookup) => {
            const names = f.genderIds
                .map((id) => lookup.genders.find((g) => g.id === id)?.value)
                .filter(Boolean)
            return names.length > 0
                ? names.join(', ')
                : pluralize(f.genderIds.length, 'selected')
        },
        getClearFields: () => ({ genderIds: [] }),
    },
    {
        key: 'ethnicityIds',
        label: 'Ethnicity',
        isActive: (f) => f.ethnicityIds.length > 0,
        getDisplayValue: (f, lookup) => {
            const names = f.ethnicityIds
                .map((id) => lookup.ethnicities.find((e) => e.id === id)?.value)
                .filter(Boolean)
            return names.length > 0
                ? names.join(', ')
                : pluralize(f.ethnicityIds.length, 'selected')
        },
        getClearFields: () => ({ ethnicityIds: [] }),
    },
    {
        key: 'citizenshipIds',
        label: 'Citizenship',
        isActive: (f) => f.citizenshipIds.length > 0,
        getDisplayValue: (f, lookup) => {
            const names = f.citizenshipIds
                .map(
                    (id) => lookup.citizenships.find((c) => c.id === id)?.value
                )
                .filter(Boolean)
            return names.length > 0
                ? names.join(', ')
                : pluralize(f.citizenshipIds.length, 'selected')
        },
        getClearFields: () => ({ citizenshipIds: [] }),
    },
    {
        key: 'visaStatusIds',
        label: 'Visa Status',
        isActive: (f) =>
            f.visaStatusIds.length > 0 || f.visaStatusIdsNot.length > 0,
        getDisplayValue: (f, lookup) => {
            const included = f.visaStatusIds
                .map(
                    (id) => lookup.visaStatuses.find((v) => v.id === id)?.value
                )
                .filter(Boolean)
            const excluded = f.visaStatusIdsNot
                .map(
                    (id) => lookup.visaStatuses.find((v) => v.id === id)?.value
                )
                .filter(Boolean)
                .map((v) => `NOT ${v}`)
            const all = [...included, ...excluded]
            return all.length > 0
                ? all.join(', ')
                : pluralize(
                      f.visaStatusIds.length + f.visaStatusIdsNot.length,
                      'selected'
                  )
        },
        getClearFields: () => ({ visaStatusIds: [], visaStatusIdsNot: [] }),
    },
    {
        key: 'qualitativeIds',
        label: 'Qualitative',
        isActive: (f) =>
            f.qualitativeIds.length > 0 || f.qualitativeIdsNot.length > 0,
        getDisplayValue: (f, lookup) => {
            const included = f.qualitativeIds
                .map(
                    (id) => lookup.qualitatives.find((q) => q.id === id)?.value
                )
                .filter(Boolean)
            const excluded = f.qualitativeIdsNot
                .map(
                    (id) => lookup.qualitatives.find((q) => q.id === id)?.value
                )
                .filter(Boolean)
                .map((v) => `NOT ${v}`)
            const all = [...included, ...excluded]
            return all.length > 0
                ? all.join(', ')
                : pluralize(
                      f.qualitativeIds.length + f.qualitativeIdsNot.length,
                      'selected'
                  )
        },
        getClearFields: () => ({ qualitativeIds: [], qualitativeIdsNot: [] }),
    },
    {
        key: 'revenueExperienceIds',
        label: 'Revenue Exp',
        isActive: (f) => f.revenueExperienceIds.length > 0,
        getDisplayValue: (f, lookup) => {
            const names = f.revenueExperienceIds
                .map(
                    (id) =>
                        lookup.revenueExperiences.find((r) => r.id === id)
                            ?.value
                )
                .filter(Boolean)
            return names.length > 0
                ? names.join(', ')
                : pluralize(f.revenueExperienceIds.length, 'selected')
        },
        getClearFields: () => ({ revenueExperienceIds: [] }),
    },
    {
        key: 'lgbtqIds',
        label: 'LGBTQ',
        isActive: (f) => f.lgbtqIds.length > 0,
        getDisplayValue: (f, lookup) => {
            const names = f.lgbtqIds
                .map((id) => lookup.lgbtq.find((l) => l.id === id)?.value)
                .filter(Boolean)
            return names.length > 0
                ? names.join(', ')
                : pluralize(f.lgbtqIds.length, 'selected')
        },
        getClearFields: () => ({ lgbtqIds: [] }),
    },
    {
        key: 'cpiPlacedIds',
        label: 'CPI Placed',
        isActive: (f) =>
            f.cpiPlacedIds.length > 0 || f.cpiPlacedIdsNot.length > 0,
        getDisplayValue: (f, lookup) => {
            const included = f.cpiPlacedIds
                .map((id) => lookup.cpiPlaced.find((c) => c.id === id)?.value)
                .filter(Boolean)
            const excluded = f.cpiPlacedIdsNot
                .map((id) => lookup.cpiPlaced.find((c) => c.id === id)?.value)
                .filter(Boolean)
                .map((v) => `NOT ${v}`)
            const all = [...included, ...excluded]
            return all.length > 0
                ? all.join(', ')
                : pluralize(
                      f.cpiPlacedIds.length + f.cpiPlacedIdsNot.length,
                      'selected'
                  )
        },
        getClearFields: () => ({ cpiPlacedIds: [], cpiPlacedIdsNot: [] }),
    },
    {
        key: 'referenceAttributeIds',
        label: 'Reference Attr',
        isActive: (f) => f.referenceAttributeIds.length > 0,
        getDisplayValue: (f, lookup) => {
            const names = f.referenceAttributeIds
                .map(
                    (id) =>
                        lookup.referenceAttributes.find((r) => r.id === id)
                            ?.value
                )
                .filter(Boolean)
            return names.length > 0
                ? names.join(', ')
                : pluralize(f.referenceAttributeIds.length, 'selected')
        },
        getClearFields: () => ({ referenceAttributeIds: [] }),
    },
    {
        key: 'undergradCollegeIds',
        label: 'Undergrad',
        isActive: (f) => f.undergradCollegeIds.length > 0,
        getDisplayValue: (f, lookup) => {
            const names = f.undergradCollegeIds
                .map((id) => lookup.colleges.find((c) => c.id === id)?.value)
                .filter(Boolean)
            return names.length > 0
                ? names.join(', ')
                : pluralize(f.undergradCollegeIds.length, 'selected')
        },
        getClearFields: () => ({ undergradCollegeIds: [] }),
    },
    {
        key: 'mbaCollegeIds',
        label: 'MBA College',
        isActive: (f) => f.mbaCollegeIds.length > 0,
        getDisplayValue: (f, lookup) => {
            const names = f.mbaCollegeIds
                .map((id) => lookup.colleges.find((c) => c.id === id)?.value)
                .filter(Boolean)
            return names.length > 0
                ? names.join(', ')
                : pluralize(f.mbaCollegeIds.length, 'selected')
        },
        getClearFields: () => ({ mbaCollegeIds: [] }),
    },
    {
        key: 'undergradCollegeNames',
        label: 'Undergrad',
        isActive: (f) => f.undergradCollegeNames.length > 0,
        getDisplayValue: (f) => f.undergradCollegeNames.join(', '),
        getClearFields: () => ({ undergradCollegeNames: [] }),
    },
    {
        key: 'graduateCollegeNames',
        label: 'Graduate',
        isActive: (f) => f.graduateCollegeNames.length > 0,
        getDisplayValue: (f) => f.graduateCollegeNames.join(', '),
        getClearFields: () => ({ graduateCollegeNames: [] }),
    },
    {
        key: 'majorIds',
        label: 'Major',
        isActive: (f) => f.majorIds.length > 0,
        getDisplayValue: (f, lookup) => {
            const names = f.majorIds
                .map((id) => lookup.majors.find((m) => m.id === id)?.value)
                .filter(Boolean)
            return names.length > 0
                ? names.join(', ')
                : pluralize(f.majorIds.length, 'selected')
        },
        getClearFields: () => ({ majorIds: [] }),
    },
    {
        key: 'degreeTypeIds',
        label: 'Degree',
        isActive: (f) =>
            f.degreeTypeIds.length > 0 || f.degreeTypeIdsNot.length > 0,
        getDisplayValue: (f, lookup) => {
            const included = f.degreeTypeIds
                .map((id) => lookup.degreeTypes.find((d) => d.id === id)?.value)
                .filter(Boolean)
            const excluded = f.degreeTypeIdsNot
                .map((id) => lookup.degreeTypes.find((d) => d.id === id)?.value)
                .filter(Boolean)
                .map((v) => `NOT ${v}`)
            const all = [...included, ...excluded]
            return all.length > 0
                ? all.join(', ')
                : pluralize(
                      f.degreeTypeIds.length + f.degreeTypeIdsNot.length,
                      'selected'
                  )
        },
        getClearFields: () => ({ degreeTypeIds: [], degreeTypeIdsNot: [] }),
    },
    {
        key: 'mbaTierIds',
        label: 'MBA Tier',
        isActive: (f) => f.mbaTierIds.length > 0,
        getDisplayValue: (f, lookup) => {
            const names = f.mbaTierIds
                .map((id) => lookup.mbaTiers.find((t) => t.id === id)?.value)
                .filter(Boolean)
            return names.length > 0
                ? names.join(', ')
                : pluralize(f.mbaTierIds.length, 'selected')
        },
        getClearFields: () => ({ mbaTierIds: [] }),
    },
    {
        key: 'gpaRange',
        label: 'GPA',
        isActive: (f) => {
            const hasRangeFilter =
                (f.gpaRangeMin !== undefined && f.gpaRangeMin !== 0) ||
                (f.gpaRangeMax !== undefined && f.gpaRangeMax !== 5)
            const hasModeFilter =
                f.gpaNoGpaMode !== undefined && f.gpaNoGpaMode !== 'include'
            return hasRangeFilter || hasModeFilter
        },
        getDisplayValue: (f) => {
            if (f.gpaNoGpaMode === 'only') return 'No GPA only'
            const min = f.gpaRangeMin ?? 0
            const max = f.gpaRangeMax ?? 5
            const isDefaultRange = min === 0 && max === 5
            const parts: string[] = []
            if (!isDefaultRange) {
                parts.push(min === max ? `${min}` : `${min} - ${max}`)
            }
            if (f.gpaNoGpaMode === 'exclude') {
                parts.push('excl. no GPA')
            }
            return parts.length > 0 ? parts.join(', ') : null
        },
        getClearFields: () => ({
            gpaRangeMin: undefined,
            gpaRangeMax: undefined,
            gpaNoGpaMode: undefined,
        }),
    },
    {
        key: 'graduationDateRange',
        label: 'Graduation',
        isActive: (f) =>
            f.graduationRangeGte !== undefined ||
            f.graduationRangeLte !== undefined ||
            f.graduationEducationType !== undefined,
        getDisplayValue: (f) => {
            const parts: string[] = []
            const gte = f.graduationRangeGte
            const lte = f.graduationRangeLte
            if (gte && lte) parts.push(`${gte} to ${lte}`)
            else if (gte) parts.push(`${gte}+`)
            else if (lte) parts.push(`≤${lte}`)
            if (f.graduationEducationType) parts.push(f.graduationEducationType)
            return parts.length > 0 ? parts.join(', ') : null
        },
        getClearFields: () => ({
            graduationRangeGte: undefined,
            graduationRangeLte: undefined,
            graduationEducationType: undefined,
        }),
    },
    {
        key: 'analystClassYear',
        label: 'Analyst Class',
        isActive: (f) =>
            f.analystClassYearGte !== undefined ||
            f.analystClassYearLte !== undefined,
        getDisplayValue: (f) => {
            const gte = f.analystClassYearGte
            const lte = f.analystClassYearLte
            if (gte !== undefined && lte !== undefined)
                return `${gte} to ${lte}`
            if (gte !== undefined) return `${gte}+`
            if (lte !== undefined) return `≤${lte}`
            return null
        },
        getClearFields: () => ({
            analystClassYearGte: undefined,
            analystClassYearLte: undefined,
        }),
    },
    {
        key: 'industryPref',
        label: 'Industry Pref',
        isActive: (f) =>
            f.industryPrefFirst.length > 0 ||
            f.industryPrefSecond.length > 0 ||
            f.industryPrefThird.length > 0,
        getDisplayValue: (f, lookup) => {
            const all = [
                ...f.industryPrefFirst,
                ...f.industryPrefSecond,
                ...f.industryPrefThird,
            ]
            const names = all
                .map((id) => lookup.industries.find((i) => i.id === id)?.value)
                .filter(Boolean)
            return names.length > 0
                ? names.join(', ')
                : pluralize(all.length, 'selected')
        },
        getClearFields: () => ({
            industryPrefFirst: [],
            industryPrefSecond: [],
            industryPrefThird: [],
        }),
    },
    {
        key: 'sectorPref',
        label: 'Sector Pref',
        isActive: (f) =>
            f.sectorPrefFirst.length > 0 ||
            f.sectorPrefSecond.length > 0 ||
            f.sectorPrefThird.length > 0,
        getDisplayValue: (f, lookup) => {
            const all = [
                ...f.sectorPrefFirst,
                ...f.sectorPrefSecond,
                ...f.sectorPrefThird,
            ]
            const names = all
                .map((id) => lookup.sectors.find((s) => s.id === id)?.value)
                .filter(Boolean)
            return names.length > 0
                ? names.join(', ')
                : pluralize(all.length, 'selected')
        },
        getClearFields: () => ({
            sectorPrefFirst: [],
            sectorPrefSecond: [],
            sectorPrefThird: [],
        }),
    },
    {
        key: 'locationPref',
        label: 'Location Pref',
        isActive: (f) =>
            f.locationPrefFirst.length > 0 ||
            f.locationPrefSecond.length > 0 ||
            f.locationPrefThird.length > 0,
        getDisplayValue: (f) => {
            const all = [
                ...f.locationPrefFirst,
                ...f.locationPrefSecond,
                ...f.locationPrefThird,
            ]
            return pluralize(all.length, 'selected')
        },
        getClearFields: () => ({
            locationPrefFirst: [],
            locationPrefSecond: [],
            locationPrefThird: [],
        }),
    },
    {
        key: 'screeningNoteTemplateIds',
        label: 'Screening Type',
        isActive: (f) => f.screeningNoteTemplateIds.length > 0,
        getDisplayValue: (f, lookup) => {
            const names = f.screeningNoteTemplateIds
                .map(
                    (id) =>
                        lookup.screeningTemplates?.find((t) => t.id === id)
                            ?.templateType
                )
                .filter(Boolean)
                .map((t) => startCase(t))
            const base =
                names.length > 0
                    ? names.join(', ')
                    : pluralize(f.screeningNoteTemplateIds.length, 'selected')
            if (f.screeningNoteStarRatingMin !== undefined) {
                return `${base} (${f.screeningNoteStarRatingMin}${f.screeningNoteStarRatingMin < 5 ? '+' : ''} ★)`
            }
            return base
        },
        getClearFields: () => ({
            screeningNoteTemplateIds: [],
            screeningNoteStarRatingMin: undefined,
            screeningNoteStarRatingMax: undefined,
        }),
    },
    {
        key: 'hometownSearchTerms',
        label: 'Hometown',
        isActive: (f) => f.hometownSearchTerms.length > 0,
        getDisplayValue: (f) => f.hometownSearchTerms.join(', '),
        getClearFields: () => ({ hometownSearchTerms: [] }),
    },
    {
        key: 'startDateRange',
        label: 'Start Date',
        isActive: (f) =>
            f.startDateGte !== undefined || f.startDateLte !== undefined,
        getDisplayValue: (f) => {
            const gte = f.startDateGte
            const lte = f.startDateLte
            if (gte && lte) return `${gte} to ${lte}`
            if (gte) return `${gte}+`
            if (lte) return `≤${lte}`
            return null
        },
        getClearFields: () => ({
            startDateGte: undefined,
            startDateLte: undefined,
        }),
    },
    {
        key: 'processStatus',
        label: 'Process Status',
        isActive: (f) =>
            f.processStatusIds.length > 0 || f.processStatusGroupIds.length > 0,
        getDisplayValue: (f, lookup) => {
            const statusNames = f.processStatusIds
                .map(
                    (id) =>
                        lookup.processStatuses.find((ps) => ps.id === id)?.label
                )
                .filter(Boolean)
            const groupNames = f.processStatusGroupIds
                .map((id) => {
                    const ps = lookup.processStatuses.find(
                        (ps) => ps.groupId === id
                    )
                    return ps?.groupLabel
                })
                .filter(Boolean)
            const all = [...new Set([...groupNames, ...statusNames])]
            return all.length > 0
                ? all.join(', ')
                : pluralize(
                      f.processStatusIds.length +
                          f.processStatusGroupIds.length,
                      'selected'
                  )
        },
        getClearFields: () => ({
            processStatusIds: [],
            processStatusGroupIds: [],
        }),
    },
]

interface ActiveChip {
    key: string
    label: string
    onDelete: () => void
}

export const CandidateTableFooterSlot: FC<{
    startContent?: React.ReactNode
}> = ({ startContent }) => {
    const apiRef = useGridApiContext()
    const { tableState, setTableState, totalItems } = useCandidateSqlSearch()
    const allColumns = apiRef.current.getAllColumns()
    const visibleColumns = apiRef.current.getVisibleColumns()
    const showCount = visibleColumns.length < allColumns.length

    const displayTotal = totalItems ?? 0

    return (
        <GridFooterContainer>
            <Button
                variant="text"
                size="small"
                onClick={() =>
                    apiRef.current.showPreferences(
                        GridPreferencePanelsValue.columns
                    )
                }
                sx={{ textTransform: 'none', fontSize: '0.75rem' }}
            >
                Columns
                {showCount &&
                    ` (${visibleColumns.length}/${allColumns.length})`}
            </Button>
            {startContent}
            <Box flex={1} />
            <TablePagination
                component="div"
                count={displayTotal}
                page={Math.max(
                    0,
                    Math.min(
                        tableState.page - 1,
                        Math.max(
                            0,
                            Math.ceil(displayTotal / tableState.limit) - 1
                        )
                    )
                )}
                rowsPerPage={tableState.limit}
                onPageChange={(_, newPage) => {
                    if (displayTotal > 0) {
                        setTableState({ page: newPage + 1 })
                    }
                }}
                onRowsPerPageChange={(e) => {
                    setTableState({
                        limit: parseInt(e.target.value, 10),
                        page: 1,
                    })
                }}
                rowsPerPageOptions={[25, 50, 100]}
            />
        </GridFooterContainer>
    )
}

export const CandidateTableToolbarSlot: FC<{
    selectedCount: number
    actionButtons?: React.ReactNode
    afterSearchContent?: React.ReactNode
    endContent?: React.ReactNode
}> = ({ selectedCount, actionButtons, afterSearchContent, endContent }) => {
    const {
        tableState,
        setTableState,
        hasActiveFilters,
        clearFilters,
        totalItems,
        selectAllCandidates,
        isSelectingAll,
        setSelectedCandidateIds,
    } = useCandidateSqlSearch()

    const filters = tableState.filters
    const setFilters = (newFilters: typeof filters) =>
        setTableState({ filters: newFilters })

    const [localQuickSearch, setLocalQuickSearch] = useState(
        filters.quickSearch || ''
    )

    useEffect(() => {
        setLocalQuickSearch(filters.quickSearch || '')
    }, [filters.quickSearch])

    useEffect(() => {
        const trimmed = localQuickSearch || undefined
        if (trimmed === filters.quickSearch) return
        const timeoutId = setTimeout(() => {
            setFilters({ ...filters, quickSearch: trimmed })
        }, 300)
        return () => clearTimeout(timeoutId)
    }, [localQuickSearch]) // eslint-disable-line react-hooks/exhaustive-deps

    const { data: companiesData } = useQuery(GetCompaniesById, {
        variables: { ids: filters.companyIds },
        skip: filters.companyIds.length === 0,
    })
    const { data: locationsData } = useQuery(GetOfficeLocationsById, {
        variables: { ids: filters.locationIds },
        skip: filters.locationIds.length === 0,
    })
    const { data: gendersData } = useQuery(GetGenderOptions, {
        skip: filters.genderIds.length === 0,
    })
    const { data: ethnicitiesData } = useQuery(GetEthnicityOptions, {
        skip: filters.ethnicityIds.length === 0,
    })
    const { data: citizenshipsData } = useQuery(GetCitizenshipOptions, {
        skip: filters.citizenshipIds.length === 0,
    })
    const { data: visaStatusesData } = useQuery(GetVisaStatusOptions, {
        skip:
            filters.visaStatusIds.length === 0 &&
            filters.visaStatusIdsNot.length === 0,
    })
    const { data: qualitativesData } = useQuery(GetQualitativeOptions, {
        skip:
            filters.qualitativeIds.length === 0 &&
            filters.qualitativeIdsNot.length === 0,
    })
    const { data: revenueExperiencesData } = useQuery(
        GetRevenueExperienceOptions,
        {
            skip: filters.revenueExperienceIds.length === 0,
        }
    )
    const { data: lgbtqData } = useQuery(GetLgbtqOptions, {
        skip: filters.lgbtqIds.length === 0,
    })
    const { data: cpiPlacedData } = useQuery(GetCpiPlacedOptions, {
        skip:
            filters.cpiPlacedIds.length === 0 &&
            filters.cpiPlacedIdsNot.length === 0,
    })
    const { data: referenceAttributesData } = useQuery(
        GetReferenceAttributeOptions,
        {
            skip: filters.referenceAttributeIds.length === 0,
        }
    )
    const { data: collegesData } = useQuery(GetColleges, {
        skip:
            filters.undergradCollegeIds.length === 0 &&
            filters.mbaCollegeIds.length === 0,
    })
    const { data: majorsData } = useQuery(GetMajorOptions, {
        skip: filters.majorIds.length === 0,
    })
    const { data: degreeTypesData } = useQuery(GetDegreeTypes, {
        skip:
            filters.degreeTypeIds.length === 0 &&
            filters.degreeTypeIdsNot.length === 0,
    })
    const { data: mbaTiersData } = useQuery(GetMbaTierOptions, {
        skip: filters.mbaTierIds.length === 0,
    })
    const { data: processStatusesData } = useQuery(GetProcessStatuses, {
        skip:
            filters.processStatusIds.length === 0 &&
            filters.processStatusGroupIds.length === 0,
    })
    const { data: industriesData } = useQuery(GetIndustryOptions)
    const { data: sectorsData } = useQuery(GetSectorOptions)
    const { data: screeningTemplatesData } = useQuery(
        GetCandidateNoteTemplates,
        {
            skip: filters.screeningNoteTemplateIds.length === 0,
        }
    )

    const lookupData: LookupData = {
        companies: companiesData?.companiesById ?? [],
        locations: locationsData?.officeLocationsById ?? [],
        genders: gendersData?.genders ?? [],
        ethnicities: ethnicitiesData?.ethnicities ?? [],
        citizenships: citizenshipsData?.citizenships ?? [],
        visaStatuses: visaStatusesData?.visaStatuses ?? [],
        qualitatives: qualitativesData?.personalityAttributes ?? [],
        revenueExperiences: revenueExperiencesData?.revenueExperiences ?? [],
        lgbtq: lgbtqData?.lgbtq ?? [],
        cpiPlaced: cpiPlacedData?.cpiPlaced ?? [],
        referenceAttributes: referenceAttributesData?.referenceAttributes ?? [],
        colleges: collegesData?.colleges ?? [],
        majors: majorsData?.majors ?? [],
        degreeTypes: degreeTypesData?.degreeTypes ?? [],
        mbaTiers: mbaTiersData?.mbaTiers ?? [],
        industries: industriesData?.industries ?? [],
        sectors: sectorsData?.sectors ?? [],
        screeningTemplates: (
            screeningTemplatesData?.noteTemplates ?? []
        ).filter((t) => t.isScreeningType),
        processStatuses: (processStatusesData?.processStatuses ?? []).map(
            (ps) => ({
                id: ps.id,
                label: ps.statusLabel,
                groupId: ps.processStatusSummaryGroup?.id ?? '',
                groupLabel: ps.processStatusSummaryGroup?.label ?? 'No Group',
            })
        ),
    }

    const activeChips: ActiveChip[] = FILTER_CONFIGS.filter((config) =>
        config.isActive(filters)
    )
        .map((config) => {
            const displayValue = config.getDisplayValue(filters, lookupData)
            if (!displayValue) return null
            return {
                key: config.key,
                label: `${config.label}: ${displayValue}`,
                onDelete: () =>
                    setFilters({ ...filters, ...config.getClearFields() }),
            }
        })
        .filter((chip): chip is ActiveChip => chip !== null)

    return (
        <Stack gap={getPxAsRem(8)}>
            <Stack
                direction="row"
                alignItems="center"
                gap={getPxAsRem(8)}
                flexWrap="wrap"
                paddingBlockStart={getPxAsRem(6)}
                paddingBlockEnd={getPxAsRem(12)}
            >
                <TextField
                    size="small"
                    placeholder="Search..."
                    data-testid="candidate-quick-search"
                    value={localQuickSearch}
                    onChange={(e) => setLocalQuickSearch(e.target.value)}
                    sx={{ width: getPxAsRem(240), ...ghostInputSx }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search
                                    sx={{
                                        fontSize: '1rem',
                                        color: 'text.secondary',
                                    }}
                                />
                            </InputAdornment>
                        ),
                    }}
                />

                {afterSearchContent}

                {activeChips.map((chip) => (
                    <Typography
                        key={chip.key}
                        component="span"
                        onClick={chip.onDelete}
                        sx={minimalTagSx}
                    >
                        {chip.label}
                        <Clear sx={{ fontSize: '0.625rem' }} />
                    </Typography>
                ))}

                <Box flex={1} />

                {hasActiveFilters && (
                    <Button
                        variant="text"
                        size="small"
                        onClick={clearFilters}
                        sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                    >
                        Clear all
                    </Button>
                )}

                {selectedCount > 0 && (
                    <Stack
                        direction="row"
                        alignItems="center"
                        gap={1}
                        sx={{
                            borderLeft: '1px solid',
                            borderColor: 'divider',
                            pl: 1.5,
                            ml: 0.5,
                        }}
                    >
                        {selectedCount < totalItems && (
                            <Button
                                variant="text"
                                size="small"
                                onClick={selectAllCandidates}
                                disabled={isSelectingAll}
                                sx={{
                                    textTransform: 'none',
                                    fontSize: '0.75rem',
                                    minWidth: 'auto',
                                }}
                            >
                                {isSelectingAll ? (
                                    <>
                                        <CircularProgress
                                            size={12}
                                            sx={{ mr: 0.5 }}
                                        />
                                        Selecting...
                                    </>
                                ) : (
                                    `Select all ${totalItems.toLocaleString()} candidates`
                                )}
                            </Button>
                        )}
                        {selectedCount === totalItems && (
                            <Typography
                                sx={{
                                    fontSize: '0.75rem',
                                    color: 'text.secondary',
                                }}
                            >
                                All {totalItems.toLocaleString()} selected
                            </Typography>
                        )}
                        <Button
                            variant="text"
                            size="small"
                            onClick={() => setSelectedCandidateIds([])}
                            sx={{
                                textTransform: 'none',
                                fontSize: '0.75rem',
                                minWidth: 'auto',
                                color: 'text.secondary',
                            }}
                        >
                            Clear ({selectedCount.toLocaleString()})
                        </Button>
                    </Stack>
                )}

                {actionButtons}

                {endContent}
            </Stack>
        </Stack>
    )
}
